/* ============================================================
   BrandContext — multi-brand "active brand" state for the SPA.

   Holds the account's brands, which one is active, and the plan's
   brands limit; exposes switch / add / refresh. Everything is gated:
   - `multiBrandEnabled()` flag OFF  → EMPTY context (enabled:false),
     no /brands fetch, api sends no brandProfileId → today's behavior.
   - flag ON but plan single-brand   → enabled:true but canMultiBrand
     is false, so the sidebar shows the LOCKED upsell, not the live
     switcher. The live switcher needs canMultiBrand.

   The active brand is pushed into the api client (setActiveBrandId) so
   research/strategy calls scope to it without threading the id everywhere.
   ============================================================ */
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { api, persistActiveBrand, clearActiveBrand, readActiveBrandOwner } from '../services/api/client.js';
import { notify } from '../lib/notifications.js';
import { multiBrandEnabled } from '../lib/flags.js';
import { useAuth } from './AuthContext';
import AddBrandFlow from '../components/AddBrandFlow.jsx';

function unwrap(res) {
  return res && res.data && typeof res.data === 'object' ? res.data : res;
}

const EMPTY = {
  enabled: false,
  brands: [],
  activeBrandId: null,
  activeBrand: null,
  brandsLimit: 1,
  canMultiBrand: false,
  atLimit: false,
  loading: false,
  switchBrand: async () => {},
  addBrand: async () => null,
  refresh: async () => {},
  addBrandOpen: false,
  openAddBrand: () => {},
  closeAddBrand: () => {},
};

const BrandContext = createContext(EMPTY);

export function useBrand() {
  return useContext(BrandContext);
}

export function BrandProvider({ children }) {
  const { user } = useAuth();
  const enabled = multiBrandEnabled();

  const [brands, setBrands] = useState([]);
  const [activeBrandId, setActiveBrandId] = useState(null);
  const [brandsLimit, setBrandsLimit] = useState(1);
  const [loading, setLoading] = useState(false);
  const [addBrandOpen, setAddBrandOpen] = useState(false);
  const openAddBrand = useCallback(() => setAddBrandOpen(true), []);
  const closeAddBrand = useCallback(() => setAddBrandOpen(false), []);

  // Persisted mirror of the active brand: the api client seeds itself from
  // this key at module load (before this provider hydrates), closing the
  // mount race where early fetches went out unscoped and pulled another
  // brand's data. The server (user_active_brand) stays the authority.
  // Namespaced per user id (persistActiveBrand/clearActiveBrand, in
  // client.js so the constructor's synchronous seed shares the exact same
  // key format) since 2026-09-08 — see the comment there for the incident.
  const persistActive = useCallback((id) => {
    persistActiveBrand(window.localStorage, user?.id ?? null, id);
  }, [user?.id]);

  const load = useCallback(async () => {
    if (!enabled || !user) return;
    setLoading(true);
    try {
      const data = unwrap(await api.brands.list());
      const list = Array.isArray(data?.brands) ? data.brands : [];
      const active = data?.activeBrandId ?? (list[0]?.id ?? null);
      setBrands(list);
      setActiveBrandId(active);
      setBrandsLimit(data?.brandsLimit ?? null);
      api.setActiveBrandId(active);
      persistActive(active);
    } catch (_) {
      // Leave defaults — the sidebar falls back to the locked/cached chip.
    } finally {
      setLoading(false);
    }
  }, [enabled, user, persistActive]);

  // Signed out OR switched to a different user than whoever this browser
  // tab's persisted brand belongs to (2026-09-08: previously only the
  // sign-out half of this ran, and the persisted key had no owner at all,
  // so a same-browser account switch could seed the NEW user's session
  // from the OLD user's still-persisted brand id for as long as it took
  // `load()` below to overwrite it). This is the "unless the stored owner
  // matches the current session user" check the constructor's synchronous
  // seed (client.js readSeedActiveBrandId) can't do itself — it runs the
  // moment the real user is known and corrects a bad optimistic seed
  // instead of trusting it for the rest of the session.
  useEffect(() => {
    if (!enabled) return;
    if (!user) {
      api.setActiveBrandId(null);
      clearActiveBrand(window.localStorage);
      return;
    }
    const storedOwner = readActiveBrandOwner(window.localStorage);
    if (storedOwner && storedOwner !== String(user.id)) {
      api.setActiveBrandId(null);
      clearActiveBrand(window.localStorage);
    }
    // `load()` (below) re-persists under this user's own key once /brands
    // resolves — no need to set a new owner here.
  }, [enabled, user]);

  useEffect(() => { load(); }, [load]);

  // Switch active brand. Optimistic (chip updates instantly); the setActive
  // call invalidates brand-scoped caches so surfaces refetch for the new brand.
  const switchBrand = useCallback(async (id) => {
    if (!id || id === activeBrandId) return;
    setActiveBrandId(id);
    api.setActiveBrandId(id);
    persistActive(id);
    try {
      await api.brands.setActive(id);
      // A brand switch is a real context swap: mounted screens fetched their
      // data on mount and won't refetch on their own (owner-reported: Media
      // kept the previous brand's logo, Content Plan kept its data). Reload
      // so EVERY surface — media, plan, research, assistant context — comes
      // back in the new brand. The persisted active id makes the very first
      // post-reload fetches already scoped.
      window.location.reload();
    } catch (err) {
      // Revert on failure so the UI never lies about the active brand — and
      // SAY SO (incident 2026-08-28: the owner sat on an expired session and
      // the silent catch made "brand switch is broken" indistinguishable
      // from "your session died — sign in again").
      notify({
        level: 'error',
        title: 'Brand switch failed',
        error: err,
        fallbackDescription: 'Your previous brand is still active — try again.',
      });
      await load();
    }
  }, [activeBrandId, load, persistActive]);

  // Create a brand → it becomes active. Caller (add-brand flow) kicks
  // research + content-plan for the new active brand afterwards.
  const addBrand = useCallback(async (payload) => {
    const res = unwrap(await api.brands.create(payload));
    const brand = res?.brand ?? null;
    if (brand?.id) {
      setActiveBrandId(brand.id);
      api.setActiveBrandId(brand.id);
      await load();
    }
    return brand;
  }, [load]);

  const value = useMemo(() => {
    if (!enabled) return EMPTY;
    const canMultiBrand = brandsLimit === null || brandsLimit > 1;
    return {
      enabled: true,
      brands,
      activeBrandId,
      activeBrand: brands.find((b) => b.id === activeBrandId) ?? null,
      brandsLimit,
      canMultiBrand,
      atLimit: brandsLimit !== null && brands.length >= brandsLimit,
      loading,
      switchBrand,
      addBrand,
      refresh: load,
      addBrandOpen,
      openAddBrand,
      closeAddBrand,
    };
  }, [enabled, brands, activeBrandId, brandsLimit, loading, switchBrand, addBrand, load, addBrandOpen, openAddBrand, closeAddBrand]);

  return (
    <BrandContext.Provider value={value}>
      {children}
      {enabled && addBrandOpen && <AddBrandFlow onClose={closeAddBrand} />}
    </BrandContext.Provider>
  );
}
